import React, { useEffect, useMemo, useState } from "react";
import { FaQuestionCircle, FaSearch } from "react-icons/fa";
import { parameterGroups } from "./OtherParameters";
import StepGuideModal from "../general/StepGuideModal";
import { useStepGuide } from "../../../hooks/useStepGuide";
import { getStepGuide } from "../../../data/stepGuides";
import { useUtilityRates } from "../../../hooks/useUtilityRates";
import RateDetailPanel from "../../data_hub/RateDetailPanel.jsx";
import { initialEnergyChargeData } from "./touRates";

export { initialEnergyChargeData } from "./touRates";

const EnergyRate = ({
  energyChargeData,
  setEnergyChargeData,
  otherParameters,
  setOtherParameters,
  evaluationMode,
  selectedDepots,
  depotUtilityRateSelections,
  setDepotUtilityRateSelections,
}) => {
  const { resolveDepotUtilityRates, hasAuth } = useUtilityRates();
  const [openHelpSection, setOpenHelpSection] = useState(null);
  const [depotRateBundles, setDepotRateBundles] = useState([]);
  const [loadingDepotRates, setLoadingDepotRates] = useState(false);
  const [utilityRateError, setUtilityRateError] = useState(null);
  const [activeDepotId, setActiveDepotId] = useState(null);
  const [rateSearchInput, setRateSearchInput] = useState("");

  const guideContent = getStepGuide("rates");
  const {
    isOpen: isGuideOpen,
    closeGuide,
    dontShowAgain,
    onDontShowAgain
  } = useStepGuide("rates", guideContent, false);

  useEffect(() => {
    if (energyChargeData.length === 0) {
      setEnergyChargeData(initialEnergyChargeData);
    }
  }, [energyChargeData, setEnergyChargeData]);

  useEffect(() => {
    if (!hasAuth || !Array.isArray(selectedDepots) || selectedDepots.length === 0) {
      setDepotRateBundles([]);
      setLoadingDepotRates(false);
      setUtilityRateError(null);
      return;
    }

    let isMounted = true;
    const fetchDepotRates = async () => {
      setLoadingDepotRates(true);
      setUtilityRateError(null);
      try {
        const resolvedDepots = await resolveDepotUtilityRates({
          depotIds: selectedDepots,
          selectedRateIds: depotUtilityRateSelections,
        });
        if (isMounted) {
          setDepotRateBundles(Array.isArray(resolvedDepots) ? resolvedDepots : []);
        }
      } catch (error) {
        if (isMounted) {
          setDepotRateBundles([]);
          setUtilityRateError("Failed to load UtilityRate options for the selected depots.");
        }
      } finally {
        if (isMounted) {
          setLoadingDepotRates(false);
        }
      }
    };

    fetchDepotRates();
    return () => {
      isMounted = false;
    };
  }, [depotUtilityRateSelections, hasAuth, resolveDepotUtilityRates, selectedDepots]);

  const handleInputChange = (id, value) => {
    setOtherParameters((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const toggleHelpSection = (sectionId) => {
    setOpenHelpSection((prev) => (prev === sectionId ? null : sectionId));
  };

  const publicChargingSection = {
    id: "public-charging",
    title: "Additional Costs for Public Charging",
    description: "Public charging stations often include additional markup costs beyond the base electricity rate. These markups help stations cover expenses and generate profit. The value here should reflect the expected extra cost per kWh on top of the energy rate.",
    parameter: parameterGroups.publicCharging.inputs[0],
  };

  const fuelSections = [
    {
      id: "diesel-fuel",
      title: "Diesel/Gasoline",
      description: "Fuel price used for the diesel/gasoline comparison baseline.",
      parameter: parameterGroups.diesel.inputs[0],
    },
  ];

  const selectedDepotCount = Array.isArray(selectedDepots) ? selectedDepots.length : 0;
  const resolvedDepotBundles = useMemo(() => (
    Array.isArray(depotRateBundles) ? depotRateBundles : []
  ), [depotRateBundles]);
  const activeBundle = useMemo(() => {
    if (!resolvedDepotBundles.length) {
      return null;
    }
    return resolvedDepotBundles.find((bundle) => bundle.depot_id === activeDepotId) || resolvedDepotBundles[0];
  }, [activeDepotId, resolvedDepotBundles]);
  const filteredRateOptions = useMemo(() => {
    const rates = Array.isArray(activeBundle?.available_rates) ? activeBundle.available_rates : [];
    const query = String(rateSearchInput || "").trim().toLowerCase();
    if (!query) {
      return rates;
    }
    return rates.filter((rate) => {
      const haystack = [
        rate?.rate_name,
        rate?.sector,
        rate?.utility_name,
      ].join(" ").toLowerCase();
      return haystack.includes(query);
    });
  }, [activeBundle, rateSearchInput]);

  const orderedRateOptions = useMemo(() => {
    const defaultRateId = activeBundle?.default_rate_id ?? null;
    if (!defaultRateId || !Array.isArray(filteredRateOptions) || filteredRateOptions.length === 0) {
      return filteredRateOptions;
    }
    const defaultIndex = filteredRateOptions.findIndex((rate) => Number(rate?.id) === Number(defaultRateId));
    if (defaultIndex <= 0) {
      return filteredRateOptions;
    }
    const next = filteredRateOptions.slice();
    const [defaultRate] = next.splice(defaultIndex, 1);
    next.unshift(defaultRate);
    return next;
  }, [activeBundle?.default_rate_id, filteredRateOptions]);

  useEffect(() => {
    if (!resolvedDepotBundles.length) {
      setActiveDepotId(null);
      return;
    }
    const hasActiveBundle = resolvedDepotBundles.some((bundle) => bundle.depot_id === activeDepotId);
    if (!hasActiveBundle) {
      setActiveDepotId(resolvedDepotBundles[0].depot_id);
    }
  }, [activeDepotId, resolvedDepotBundles]);

  useEffect(() => {
    setRateSearchInput("");
  }, [activeDepotId]);

  const renderCompactInput = (parameter) => {
    if (!parameter) {
      return null;
    }

    const currentValue = otherParameters?.[parameter.id] ?? parameter.defaultValue ?? "";

    return (
      <div className="mt-3">
        <div className="relative max-w-xs">
          <input
            type="number"
            step="any"
            inputMode="decimal"
            value={currentValue}
            onChange={(event) => handleInputChange(
              parameter.id,
              event.target.value === "" ? 0 : Number(event.target.value)
            )}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {parameter.unit && (
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-slate-500">
              {parameter.unit}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white p-5 rounded-lg shadow-md">
      <div className="space-y-4">
        <section className="space-y-3">
          <h3 className="text-base font-semibold text-slate-900">Electricity</h3>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-tutorial-id="rate-depot-rates-card">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-slate-900">Depot Utility Rates</h4>
                  <button
                    type="button"
                    onClick={() => toggleHelpSection("utility-rates")}
                    className="relative -top-1 text-slate-400 transition hover:text-slate-600"
                    aria-label="Show help for depot utility rates"
                  >
                    <FaQuestionCircle className="text-[11px]" />
                  </button>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {selectedDepotCount} depot{selectedDepotCount === 1 ? "" : "s"} selected
                </p>
              </div>
            </div>

            {openHelpSection === "utility-rates" && (
              <div className="mt-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
                Review the automatically detected utility for each depot, choose a different rate for that same utility when needed, and inspect the resolved Energy Charge and Demand Charge tables below.
              </div>
            )}

            {selectedDepotCount === 0 && (
              <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-600">
                Select at least one depot in the previous step to review UtilityRate options here.
              </div>
            )}

            {selectedDepotCount > 0 && loadingDepotRates && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-600">
                Loading UtilityRate options for the selected depots...
              </div>
            )}

            {selectedDepotCount > 0 && utilityRateError && (
              <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {utilityRateError}
              </div>
            )}

            {resolvedDepotBundles.length > 0 && (
              <div className="mt-3">
                {activeBundle && (
                  <div className="mt-4">
                    <div className="grid gap-4 lg:grid-cols-[minmax(320px,380px)_1fr]">
                      <div className="space-y-4">
                        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                          <label
                            htmlFor="energy-rate-depot-select"
                            className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500"
                          >
                            Depot
                          </label>
                          <div className="mt-3">
                            <select
                              id="energy-rate-depot-select"
                              value={String(activeBundle.depot_id)}
                              onChange={(event) => {
                                const nextBundle = resolvedDepotBundles.find(
                                  (bundle) => String(bundle.depot_id) === event.target.value
                                );
                                setActiveDepotId(nextBundle?.depot_id ?? event.target.value);
                              }}
                              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              {resolvedDepotBundles.map((bundle, index) => (
                                <option key={bundle.depot_id} value={String(bundle.depot_id)}>
                                  Depot {index + 1}: {bundle.depot_name || bundle.depot_id}
                                </option>
                              ))}
                            </select>
                          </div>
                          {activeBundle.mapped_utility_name && activeBundle.mapped_utility_name !== activeBundle.utility_name && (
                            <p className="mt-1 text-sm text-slate-500">
                              Rate catalog: {activeBundle.mapped_utility_name}
                            </p>
                          )}
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                          <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Search Rates
                          </label>
                          <p className="mt-2 text-sm text-slate-600">
                            Utility: {activeBundle.utility_name || "Unknown"}
                          </p>
                          <div className="relative mt-3">
                            <input
                              type="text"
                              value={rateSearchInput}
                              onChange={(event) => setRateSearchInput(event.target.value)}
                              placeholder="Search by rate name or sector"
                              className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <FaSearch className="absolute left-3 top-3.5 text-xs text-slate-400" />
                          </div>

                          <div className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1">
                            {orderedRateOptions.length > 0 ? orderedRateOptions.map((rate) => {
                              const isSelected = Number(activeBundle.selected_rate_id) === Number(rate.id);
                              const isDefault = Number(activeBundle.default_rate_id) === Number(rate.id);
                              return (
                                <button
                                  key={rate.id}
                                  type="button"
                                  onClick={() => {
                                    setDepotUtilityRateSelections((prev) => ({
                                      ...prev,
                                      [activeBundle.depot_id]: Number(rate.id),
                                    }));
                                  }}
                                  className={`w-full rounded-xl border px-3 py-3 text-left transition ${
                                    isSelected
                                      ? "border-blue-500 bg-blue-50 shadow-sm"
                                      : "border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100"
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <p className={`text-sm font-medium ${isSelected ? "text-blue-900" : "text-slate-800"}`}>
                                      {rate.rate_name}
                                    </p>
                                    <div className="flex shrink-0 items-center gap-1">
                                      {rate.is_historical && (
                                        <span
                                          className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                            isSelected
                                              ? "border-amber-200 bg-amber-100 text-amber-800"
                                              : "border-amber-200 bg-white text-amber-700"
                                          }`}
                                        >
                                          Historical
                                        </span>
                                      )}
                                      {isDefault && (
                                        <span
                                          className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                            isSelected
                                              ? "border-blue-200 bg-blue-100 text-blue-800"
                                              : "border-slate-200 bg-white text-slate-600"
                                          }`}
                                        >
                                          Default
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <p className="mt-1 text-xs text-slate-500">
                                    {rate.sector || "Unknown sector"}{rate.effective_date ? ` • ${rate.effective_date}` : ""}
                                  </p>
                                </button>
                              );
                            }) : (
                              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                                No matching rates found for this utility.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="min-w-0">
                        {activeBundle.selected_rate ? (
                          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                            <RateDetailPanel rateData={activeBundle.selected_rate} />
                          </div>
                        ) : (
                          <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-4 text-sm text-slate-600">
                            No UtilityRate details are available for this depot.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-tutorial-id="rate-additional-costs">
            <div className="flex items-start gap-1.5 border-b border-slate-100 pb-3">
              <h4 className="text-sm font-semibold text-slate-900">{publicChargingSection.title}</h4>
              <button
                type="button"
                onClick={() => toggleHelpSection(publicChargingSection.id)}
                className="relative -top-1 text-slate-400 transition hover:text-slate-600"
                aria-label={`Show help for ${publicChargingSection.title}`}
              >
                <FaQuestionCircle className="text-[11px]" />
              </button>
            </div>

            {openHelpSection === publicChargingSection.id && (
              <div className="mt-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
                {publicChargingSection.description}
              </div>
            )}

            {renderCompactInput(publicChargingSection.parameter)}
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-base font-semibold text-slate-900">Fuel</h3>
          <div className="space-y-3">
            {fuelSections.map((section) => (
              <div key={section.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start gap-1.5 border-b border-slate-100 pb-3">
                  <h4 className="text-sm font-semibold text-slate-900">{section.title}</h4>
                  <button
                    type="button"
                    onClick={() => toggleHelpSection(section.id)}
                    className="relative -top-1 text-slate-400 transition hover:text-slate-600"
                    aria-label={`Show help for ${section.title}`}
                  >
                    <FaQuestionCircle className="text-[11px]" />
                  </button>
                </div>

                {openHelpSection === section.id && (
                  <div className="mt-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
                    {section.description}
                  </div>
                )}

                {renderCompactInput(section.parameter)}
              </div>
            ))}
          </div>
        </section>
      </div>

      {guideContent && (
        <StepGuideModal
          isOpen={isGuideOpen}
          onClose={closeGuide}
          title={guideContent.title}
          description={guideContent.description}
          bullets={guideContent.bullets}
          dontShowAgain={dontShowAgain}
          onDontShowAgain={onDontShowAgain}
        />
      )}
    </div>
  );
};

export default EnergyRate; 